(() => {
  const STORAGE_KEY = 'serenova-buying-guide';
  const guideControllers = new WeakMap();

  const initialize = (guide) => {
    if (guideControllers.has(guide)) return guideControllers.get(guide);
    if (guide.dataset.buyingGuideReady === 'true') return;

    const form = guide.querySelector('[data-guide-form]');
    const steps = Array.from(guide.querySelectorAll('[data-guide-step]'));
    const navButtons = Array.from(guide.querySelectorAll('[data-guide-nav]'));
    const backButton = guide.querySelector('[data-guide-back]');
    const nextButton = guide.querySelector('[data-guide-next]');
    const stepCount = guide.querySelector('[data-guide-step-count]');
    const selectionCount = guide.querySelector('[data-guide-selection-count]');
    const notice = guide.querySelector('[data-guide-notice]');
    const menuToggle = guide.querySelector('[data-guide-menu-toggle]');
    const menuCount = guide.querySelector('[data-guide-menu-count]');
    const progress = guide.querySelector('.buying-guide__progress');
    const shell = guide.querySelector('.buying-guide__shell');
    const mobile = window.matchMedia('(max-width: 640px)');
    const isConsumer = guide.dataset.guideConsumer === 'true';
    const isDesignMode = Boolean(window.Shopify?.designMode);
    let currentStep = 0;
    let furthestStep = 0;
    let exploringRadio = false;

    if (!form || steps.length === 0 || !backButton || !nextButton) return;

    guide.dataset.buyingGuideReady = 'true';
    const events = new AbortController();
    const eventOptions = { signal: events.signal };
    const defaultNextLabel = nextButton.textContent.trim() || 'Continue';
    const finishLabel = isConsumer && guide.querySelector('[data-guide-shopping]')
      ? 'Continue to contact details'
      : guide.dataset.guideFinishLabel?.trim() || 'Continue to contact form';
    const selectedInputs = (step) => Array.from(step.querySelectorAll('input:checked'));

    if (isConsumer && !isDesignMode) {
      steps.filter((step) => step.dataset.stepMode === 'single').forEach((step) => {
        const hint = step.querySelector('.buying-guide__auto-hint') || document.createElement('p');
        hint.className = 'buying-guide__auto-hint';
        hint.id = `${step.id}-AutoHint`;
        hint.textContent = guide.dataset.guideAutoHint || 'Choose one to continue automatically.';
        step.querySelector('legend')?.insertAdjacentElement('afterend', hint);
        const descriptions = new Set((step.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        descriptions.add(hint.id);
        step.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
      });
    }

    const closeMenu = () => {
      progress?.classList.remove('is-open');
      menuToggle?.setAttribute('aria-expanded', 'false');
    };

    const updateSelectionCount = () => {
      const count = selectedInputs(steps[currentStep]).length;
      selectionCount.textContent = !isConsumer && steps[currentStep].dataset.stepMode === 'multiple' && count > 0
        ? `${count} selected`
        : '';
      const step = steps[currentStep];
      nextButton.hidden = isConsumer && !isDesignMode && step.dataset.stepMode === 'single'
        && count === 0 && Boolean(step.querySelector('input[type="radio"]:not(:disabled)'));
    };

    const showStep = (index, { focus = true } = {}) => {
      currentStep = Math.max(0, Math.min(index, steps.length - 1));
      furthestStep = Math.max(furthestStep, currentStep);

      steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== currentStep;
      });

      navButtons.forEach((button, buttonIndex) => {
        const isCurrent = buttonIndex === currentStep;
        button.disabled = !isDesignMode && buttonIndex > furthestStep;
        button.classList.toggle('is-current', isCurrent);
        button.classList.toggle('is-complete', buttonIndex < furthestStep);
        if (isCurrent) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });

      backButton.hidden = currentStep === 0;
      nextButton.textContent = currentStep === steps.length - 1
        ? isConsumer && guide.dataset.guideContactSubmitted === 'true' ? 'Update my recommendations' : finishLabel
        : defaultNextLabel;
      stepCount.textContent = `Step ${currentStep + 1} of ${steps.length}`;
      if (menuCount) menuCount.textContent = stepCount.textContent;
      closeMenu();
      notice.hidden = true;
      updateSelectionCount();

      if (focus) {
        steps[currentStep].querySelector('legend')?.focus({ preventScroll: true });
        (isConsumer && shell ? shell : guide).scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    };

    const validateCurrentStep = () => {
      const step = steps[currentStep];
      const hasEnabledChoices = step.querySelector('input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled)');
      if (isDesignMode || !hasEnabledChoices || !step.hasAttribute('data-required-choice') || selectedInputs(step).length > 0) return true;
      notice.textContent = 'Choose one option to continue.';
      notice.hidden = false;
      step.querySelector('input')?.focus();
      return false;
    };

    const collectAnswers = () => steps.map((step) => ({
      question: step.dataset.question || step.querySelector('legend')?.textContent.trim() || 'Selection',
      values: selectedInputs(step).map((input) => input.value)
    }));

    const completeGuide = () => {
      if (isDesignMode) return;

      const guideState = {
        version: 1,
        completedAt: new Date().toISOString(),
        answers: collectAnswers()
      };

      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(guideState));
      } catch (error) {
        // The contact page still opens when browser storage is unavailable.
      }

      if (isConsumer && !guide.dispatchEvent(new CustomEvent('guide:complete', {
        cancelable: true,
        detail: guideState
      }))) return;

      const contactUrl = new URL(guide.dataset.contactUrl || '/pages/contact', window.location.origin);
      contactUrl.searchParams.set('from', 'buying-guide');
      contactUrl.hash = 'ContactForm';
      window.location.assign(contactUrl.toString());
    };

    form.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.closest('[data-guide-step]') !== steps[currentStep]) return;
      updateSelectionCount();
      notice.hidden = true;
    }, eventOptions);

    const advance = () => {
      if (!validateCurrentStep()) return;
      if (currentStep === steps.length - 1) completeGuide();
      else showStep(currentStep + 1);
    };

    const isSingleChoice = (input) => isConsumer && !isDesignMode
      && input instanceof HTMLInputElement && input.type === 'radio' && !input.disabled
      && input.closest('[data-guide-step]') === steps[currentStep]
      && steps[currentStep].dataset.stepMode === 'single';

    // Activation commits a radio choice; arrow keys can still explore its options.
    form.addEventListener('click', (event) => {
      if (!exploringRadio && isSingleChoice(event.target) && event.target.checked) advance();
    }, eventOptions);
    form.addEventListener('keydown', (event) => {
      exploringRadio = isSingleChoice(event.target) && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
      if (event.key !== 'Enter' || !isSingleChoice(event.target)) return;
      event.preventDefault();
      event.target.checked = true;
      advance();
    }, eventOptions);
    window.addEventListener('keyup', () => { exploringRadio = false; }, eventOptions);
    form.addEventListener('pointerdown', () => { exploringRadio = false; }, eventOptions);

    nextButton.addEventListener('click', advance, eventOptions);

    menuToggle?.addEventListener('click', () => {
      const open = progress.classList.toggle('is-open');
      menuToggle.setAttribute('aria-expanded', String(open));
    }, eventOptions);
    progress?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !mobile.matches || !progress.classList.contains('is-open')) return;
      event.preventDefault();
      closeMenu();
      menuToggle?.focus();
    }, eventOptions);
    mobile.addEventListener('change', () => {
      closeMenu();
      if (isConsumer && mobile.matches && progress?.contains(document.activeElement)) menuToggle?.focus();
      else if (!mobile.matches && document.activeElement === menuToggle) navButtons[currentStep]?.focus();
    }, eventOptions);

    backButton.addEventListener('click', () => showStep(currentStep - 1), eventOptions);

    navButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.stepIndex);
        if (Number.isInteger(index) && (isDesignMode || index <= furthestStep)) showStep(index);
      }, eventOptions);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      nextButton.click();
    }, eventOptions);

    showStep(0, { focus: false });

    const controller = {
      selectBlock: (block) => {
        const index = steps.indexOf(block.closest('[data-guide-step]'));
        if (index < 0) return;
        showStep(index, { focus: false });
        block.scrollIntoView({ block: 'nearest' });
      },
      destroy: () => {
        events.abort();
        delete guide.dataset.buyingGuideReady;
        guideControllers.delete(guide);
      }
    };
    guideControllers.set(guide, controller);
    return controller;
  };

  const guidesWithin = (scope) => [
    ...(scope.matches?.('[data-buying-guide]') ? [scope] : []),
    ...scope.querySelectorAll('[data-buying-guide]')
  ];
  const initializeAll = (scope = document) => guidesWithin(scope).forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    guidesWithin(event.target).forEach((guide) => guideControllers.get(guide)?.destroy());
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (!window.Shopify?.designMode || !(event.target instanceof Element)) return;
    const guide = event.target.closest('[data-buying-guide]');
    if (guide) initialize(guide)?.selectBlock(event.target);
  });
})();
